import {useApolloClient} from '@shopify/react-graphql';
import {useToggle} from '@shopify/react-hooks';
import {useEffect, useState, useCallback} from 'react';
import {TranslatableResourceType} from 'types';
import {parseGidType} from '@shopify/admin-graphql-api-utilities';
import snakeCase from 'lodash/snakeCase';
import {useShop} from '@web-utilities/shop';
import {lastArrayItem} from 'packages/@web-utilities/other';
import {
  ShopLocale,
  useShopLocalesAndMarkets,
} from 'utilities/use-shop-locales-and-markets';
import {useUrlParams} from 'utilities/use-url-params';

import TranslatableResourcesByIdsQuery, {
  TranslatableResourcesByIdsQueryData,
} from '../../graphql/TranslatableResourcesByIdsQuery.graphql';
import {
  TranslatableResource,
  TranslatableResourceInformation,
  MarketLocalizableResource,
} from '../../types';

import {splitTranslatableResourceIds} from './utils';
import {
  fetchFilterResourceIds,
  fetchMenuItemResourceIds,
  fetchMetafieldResourceIds,
  fetchMetaobjectOverrides,
  fetchProductResourceIds,
  fetchDeliveryMethodDefinitions,
} from './fetchers';

interface Props {
  resourceId?: string;
  resourceType: TranslatableResourceType;
  locale?: ShopLocale;
  marketId: string | undefined;
}

const MARKET_LOCALIZABLE_RESOURCE_TYPES = [
  TranslatableResourceType.Metafield,
  TranslatableResourceType.Metaobject,
];
const TRANSLATABLE_METAFIELD_RESOURCES = [
  TranslatableResourceType.Product,
  TranslatableResourceType.Collection,
  TranslatableResourceType.OnlineStoreArticle,
  TranslatableResourceType.OnlineStoreBlog,
  TranslatableResourceType.OnlineStorePage,
  TranslatableResourceType.Shop,
];

export function useQueryTranslatableResources({
  resourceId,
  resourceType,
  locale,
  marketId,
}: Props) {
  const apollo = useApolloClient();
  const {
    value: refetching,
    setFalse: stopRefetching,
    setTrue: startRefetching,
  } = useToggle(false);
  const {
    value: loading,
    setFalse: stopLoading,
    setTrue: startLoading,
  } = useToggle(false);
  const {loading: shopLoading} = useShop();
  const {
    loading: shopLocalesAndMarketsLoading,
    currentLocale,
    currentMarket,
  } = useShopLocalesAndMarkets();

  const [resourceInformation, setResourceInformation] = useState<
    TranslatableResourceInformation[]
  >([]);
  const [translatableResources, setTranslatableResources] = useState<
    TranslatableResource[]
  >([]);
  const [marketLocalizableResources, setMarketLocalizableResources] = useState<
    MarketLocalizableResource[]
  >([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [beforeId, setBeforeId] = useState<string | undefined>(undefined);
  const [afterId, setAfterId] = useState<string | undefined>(undefined);
  const {urlParams} = useUrlParams();

  const fetchResourceInformation = useCallback(async () => {
    if (!resourceId) return;

    // Create the basic resource information object
    // Then, append resource-specific information
    const resourceInformation: TranslatableResourceInformation[] = [
      {resourceId},
    ];
    const resourceInformationRequests = [];

    if (resourceType === TranslatableResourceType.DeliveryProfile) {
      // We don't wanna request a DeliveryProfile translatable resource from Admin API
      // Instead, we replace the DeliveryProfile with the DeliveryMethodDefinitions
      resourceInformation.pop();
      resourceInformationRequests.push(
        fetchDeliveryMethodDefinitions(apollo, resourceId),
      );
    }

    if (resourceType === TranslatableResourceType.Product) {
      resourceInformationRequests.push(
        fetchProductResourceIds(apollo, resourceId),
      );
    }

    if (resourceType === TranslatableResourceType.OnlineStoreMenu) {
      resourceInformationRequests.push(
        fetchMenuItemResourceIds(apollo, resourceId),
      );
    }

    if (resourceType === TranslatableResourceType.OnlineStoreFilterSetting) {
      resourceInformationRequests.push(
        fetchFilterResourceIds(apollo, resourceId),
      );
    }

    if (resourceType === TranslatableResourceType.Metaobject) {
      resourceInformationRequests.push(
        fetchMetaobjectOverrides(apollo, resourceId),
      );
    }

    if (TRANSLATABLE_METAFIELD_RESOURCES.includes(resourceType)) {
      resourceInformationRequests.push(
        fetchMetafieldResourceIds(apollo, resourceId),
      );
    }

    if (resourceInformationRequests.length > 0) {
      const informationRequests = await Promise.all(
        resourceInformationRequests,
      );

      informationRequests.forEach((informationRequest) => {
        if ('metaobjectOverrides' in informationRequest) {
          resourceInformation[0] = {
            ...resourceInformation[0],
            resourceAdminIdType: informationRequest.definition?.type,
            overrides: informationRequest.metaobjectOverrides,
          };
        } else {
          resourceInformation.push(...informationRequest);
        }
      });
    }

    setResourceInformation(resourceInformation);
  }, [apollo, resourceId, resourceType]);

  const fetchTranslatableResources = useCallback(
    async (direction: 'next' | 'previous' = 'next', cursorId?: string) => {
      if (
        shopLocalesAndMarketsLoading ||
        urlParams.shopLocale !== currentLocale?.locale ||
        urlParams.market !== currentMarket?.handle
      )
        return;
      if (!resourceInformation.length || !locale) return;

      const paginationVariables = {
        afterId: direction === 'next' ? cursorId : undefined,
        beforeId: direction === 'previous' ? cursorId : undefined,
      };

      const translatableResourceIds = splitTranslatableResourceIds(
        resourceInformation.map(({resourceId}) => resourceId),
        paginationVariables.afterId,
        paginationVariables.beforeId,
      );

      const marketLocalizableResourceIds = [];

      if (marketId) {
        const marketLocalizableResourceTypes =
          MARKET_LOCALIZABLE_RESOURCE_TYPES;
        const localizableResourceIds = translatableResourceIds.filter(
          (resourceId) =>
            marketLocalizableResourceTypes.includes(
              snakeCase(
                parseGidType(resourceId),
              ).toUpperCase() as TranslatableResourceType,
            ),
        );
        marketLocalizableResourceIds.push(...localizableResourceIds);
      }

      const {data} = await apollo.query<
        TranslatableResourcesByIdsQueryData,
        TranslatableResourcesByIdsQueryData.Variables
      >({
        query: TranslatableResourcesByIdsQuery,
        fetchPolicy: 'network-only',
        variables: {
          locale: locale.locale,
          marketId: marketId || '',
          includeMarketTranslations: Boolean(marketId),
          translatableResourceIds,
          marketLocalizableResourceIds,
        },
      });

      const getTranslatableResourcesById = (): TranslatableResource[] => {
        return data.translatableResourcesByIds.map((rawData) => ({
          ...rawData,
          ...resourceInformation.find(
            ({resourceId}) => resourceId === rawData?.resourceId,
          ),
        })) as TranslatableResource[];
      };

      const getMarketLocalizableResourcesById =
        (): MarketLocalizableResource[] =>
          (data.marketLocalizableResourcesByIds || []).map((rawData) => ({
            ...rawData,
            ...resourceInformation.find(
              ({resourceId}) => resourceId === rawData.resourceId,
            ),
          }));

      setMarketLocalizableResources(getMarketLocalizableResourcesById());
      setTranslatableResources(getTranslatableResourcesById());
      setAfterId(lastArrayItem(translatableResourceIds));
      setBeforeId(translatableResourceIds[0]);
      setHasNextPage(
        lastArrayItem(translatableResourceIds) !==
          lastArrayItem(resourceInformation.map(({resourceId}) => resourceId)),
      );
      setHasPreviousPage(
        translatableResourceIds[0] !== resourceInformation[0]?.resourceId,
      );
    },
    [
      apollo,
      locale,
      marketId,
      resourceInformation,
      shopLocalesAndMarketsLoading,
      currentLocale?.locale,
      currentMarket?.handle,
      urlParams.shopLocale,
      urlParams.market,
    ],
  );

  const fetchPreviousPage = useCallback(async () => {
    startRefetching();
    await fetchTranslatableResources('previous', beforeId);
    stopRefetching();
  }, [startRefetching, fetchTranslatableResources, beforeId, stopRefetching]);

  const fetchNextPage = useCallback(async () => {
    startRefetching();
    await fetchTranslatableResources('next', afterId);
    stopRefetching();
  }, [afterId, fetchTranslatableResources, startRefetching, stopRefetching]);

  useEffect(() => {
    setResourceInformation([]);
    setTranslatableResources([]);
    setMarketLocalizableResources([]);

    if (!resourceId || shopLoading) return;
    startLoading();

    (async () => {
      await fetchResourceInformation();
    })();
  }, [fetchResourceInformation, resourceId, shopLoading, startLoading]);

  useEffect(() => {
    if (!resourceInformation.length || !locale || (locale.primary && !marketId))
      return;
    startLoading();

    (async () => {
      await fetchTranslatableResources();
      stopLoading();
    })();
  }, [
    fetchTranslatableResources,
    locale,
    marketId,
    resourceInformation.length,
    startLoading,
    stopLoading,
  ]);

  return {
    resourceInformation,
    translatableResources,
    marketLocalizableResources,
    refetch: async () => {
      startRefetching();
      await fetchTranslatableResources();
      stopRefetching();
    },
    loading,
    refetching,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
  };
}
